<template>
  <div>
    <!-- Admin "Today" redesign (design 3a, Phase A2 — plan: .claude/plans/eager-painting-hejlsberg.md).
         Super Admins get the new branch unless they switch; offices follow STAGE.admin (src/utils/followups/uiVersion.js); the v-else
         branch below is the classic page, unchanged. The page still owns its data (init's
         ADMIN_DASHBOARD result, the task fetch); the new branch only binds it to the shared components. -->
    <div v-if="fuUseNewUi" class="fu-admin" data-test="fu-admin-panel">
      <followups-ui-toggle target="old" page-key="admin" @toggle="fuSetUi" />
      <header class="fu-admin__head">
        <div class="fu-admin__intro">
          <div class="fu-admin__eyebrow" data-test="fu-admin-date">{{ fuEyebrow }}</div>
          <h1 class="fu-admin__title" data-test="fu-admin-greeting">{{ fuGreeting }}</h1>
          <p class="fu-admin__lede">Log a follow-up and it moves to the done column, so you can watch the day add up.</p>
        </div>
        <div class="fu-admin__controls">
          <div class="fu-admin__scope" role="group" aria-label="Whose follow-ups" data-test="fu-admin-scope">
            <button
              type="button"
              class="fu-admin__seg"
              :class="{ 'fu-admin__seg--on': fuScope === 'everyone' }"
              :aria-pressed="fuScope === 'everyone' ? 'true' : 'false'"
              data-test="fu-admin-scope-everyone"
              @click="fuScope = 'everyone'"
            >Everyone</button>
            <button
              type="button"
              class="fu-admin__seg"
              :class="{ 'fu-admin__seg--on': fuScope === 'mine' }"
              :aria-pressed="fuScope === 'mine' ? 'true' : 'false'"
              data-test="fu-admin-scope-mine"
              @click="fuScope = 'mine'"
            >My queues</button>
          </div>
          <label class="fu-admin__sort">
            <span class="fu-admin__sr">Sort the worklist</span>
            <select v-model="fuSort" class="fu-admin__select" data-test="fu-admin-sort">
              <option v-for="o in fuSortOptions" :key="o.key" :value="o.key">{{ o.label }}</option>
            </select>
            <fu-icon name="chev" :size="16" class="fu-admin__sort-chev" />
          </label>
        </div>
      </header>

      <p v-if="fuNoQueues && !fuBusy" class="fu-admin__note" data-test="fu-admin-no-queues">
        You are not the responsible person on any follow-up queue yet, so My queues is empty. Your tasks still show.
      </p>

      <div class="fu-admin__body">
        <admin-worklist
          class="fu-admin__main"
          :open-items="fuSplit.open"
          :tasks="fuTasks"
          :visible-queue-keys="fuWorklistQueueKeys"
          :sort="fuSort"
          :busy="fuBusy"
          :today="fuToday"
          @open-task="fuOpenTask"
          @saved="fuScheduleRefresh"
          @call-goals-updated="fuScheduleRefresh"
        />
        <aside class="fu-admin__side" aria-label="Done today">
          <div v-if="fuBusy" class="fu-admin__railskel" data-test="fu-admin-rail-skeleton" aria-hidden="true">
            <span class="fu-admin__skel fu-admin__skel--title"></span>
            <span class="fu-admin__skel fu-admin__skel--sub"></span>
            <span v-for="n in 4" :key="n" class="fu-admin__skel fu-admin__skel--row"></span>
          </div>
          <admin-done-rail
            v-else
            :rail="fuRail"
            :goals="fuGoals"
            :employees="employees || []"
            :user-i-d="userID"
            :time-zone="fuTimeZone"
            :tasks="fuDoneTasks"
            @open-task="fuOpenTask"
          />
        </aside>
      </div>
    </div>
    <template v-else>
    <followups-ui-toggle target="new" page-key="admin" @toggle="fuSetUi" />
    <h2 class="page-title mb-1 text-center">
      Today
    </h2>

    <AdminPageSkeleton v-if="busy || !allSettings" />
    <div v-show="!busy && allSettings" class="btn-toolbar justify-content-end mb-xs">
      <b-button-group>
        <b-button variant="gray" class="active"
        v-b-tooltip.bottom
                v-b-tooltip.html
                title="<p class='tooltip-txt'>Block View of Important Systems</p>" 
        >
          <i class="fa fa-th-large" />
        </b-button>
        <router-link :to="{ name: 'AdminByEmployeePage'}" tag="span">
          <b-button variant="gray" v-b-tooltip.bottom
                  v-b-tooltip.html
                  title="<p class='tooltip-txt'>Vertical Column View of Systems by Employees</p>
                " >
            <i class="fa fa fa-columns" />
          </b-button>
        </router-link>
      </b-button-group>
    </div>

    <b-row v-if="todaysTasksForEmployee(userID)" v-show="!busy && allSettings">
      <b-col lg="12" xs = "12">
        <Widget>
          <b-row>
            <b-col xs="12">
              <h5><span class='fw-semi-bold'>My Tasks</span></h5>
            </b-col>
          </b-row>          
          <!-- <AdminTable v-if="myCreatedTasks" :data="myCreatedTasks" :columns="mytasksColumns" :options="options" /> -->
          <b-row v-if="todaysTasksForEmployee(userID).length === 0">
            <span class="ml-4">No tasks due today! Click <router-link to="/app/todo">here</router-link> to create a task</span>
          </b-row>
          <todo-item
            v-for="(item, index) in todaysTasksForEmployee(userID)"
            :key="item.key"
            :item="item"
            :moreThanOneItems="todaysTasksForEmployee(userID).length > 1"
            :isLoading="allMyTasksLoadingStatus[index]"
            @edit="editTaskItem($event, index, 'allMyTasksDueToday')"
            @change="handleItemChange"
            @delete="deleteTaskItem"
            @deleteThisInstanceOnly="deleteThisInstanceOnly"
          />
       </Widget>
      </b-col>
    </b-row>

    <b-row v-if="allSettings && employees" v-show="!busy && allSettings">
     <b-col lg="6" xs="12">
        <Widget>
          <b-row>
            <b-col xs="7">
              <h5><span class='fw-semi-bold'>Overdue Recall</span></h5>
            </b-col>
            <b-col xs="5">
              <div class="post-user mt-n-sm">
                  <span class="thumb-xs mr mt-n-sm">
                    <!--<img class="rounded-circle" src='../../assets/people/a4.jpg' alt="..." />-->
                    <span class="glyphicon glyphicon-user"></span>
                  </span>
                  <p class="fw-semi-bold mb-xs pull-right responsible-person-badge" v-if="overdueRecallResponsible" v-b-tooltip.hover :title="overdueRecallResponsible.all.join(', ')">
                    {{ overdueRecallResponsible.first }}<span v-if="overdueRecallResponsible.count > 1" class="responsible-count">&nbsp;+{{ overdueRecallResponsible.count - 1 }}</span>
                  </p>
              </div>
            </b-col>
          </b-row>
          <template v-if="$can('view', 'overdue_recall') || $can('view', 'all')">
            <div class="clearfix fs-mini mt" v-if="allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL.PropertyValue > 0">
              <span class="pull-right m-0 fw-semi-bold">Daily Goal: {{allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL.PropertyValue}} Calls</span>
              <span class="fs-mini">{{overdueRecallDataPercentCompleted}}% Completed</span>
            </div>
            <b-progress v-if="allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL.PropertyValue > 0" variant="success" class="bg-gray-lighter progress-xs" :value="overdueRecallDataPercentCompleted" :max="100" />
            <AdminTable v-if="overdueRecallDataToday" :data="overdueRecallDataToday" :columns="overdueRecallColumns" :options="options" :extra="this.getExtraParams('overdue-recall')" />
          </template>
          <EmptyState v-else :restricted="true" subject="overdue_recall" />
       </Widget>
     </b-col>
     <b-col lg="6" xs="12">
        <Widget>
          <b-row>
            <b-col xs="7">
              <h5><span class='fw-semi-bold'>Unscheduled Treatment</span></h5>
            </b-col>
            <b-col xs="5">
              <div class="post-user mt-n-sm">
                  <span class="thumb-xs mr mt-n-sm">
                    <!--<img class="rounded-circle" src='../../assets/people/a4.jpg' alt="..." />-->
                    <span class="glyphicon glyphicon-user"></span>
                  </span>
                  <p class="fw-semi-bold mb-xs pull-right responsible-person-badge" v-if="unscheduledTreatmentResponsible" v-b-tooltip.hover :title="unscheduledTreatmentResponsible.all.join(', ')">
                    {{ unscheduledTreatmentResponsible.first }}<span v-if="unscheduledTreatmentResponsible.count > 1" class="responsible-count">&nbsp;+{{ unscheduledTreatmentResponsible.count - 1 }}</span>
                  </p>
              </div>
            </b-col>
          </b-row>
          <template v-if="$can('view', 'unscheduled_treatment') || $can('view', 'all')">
            <div class="clearfix fs-mini mt" v-if="allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL.PropertyValue > 0">
              <span class="pull-right m-0 fw-semi-bold">Daily Goal: {{allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL.PropertyValue}} Calls</span>
              <span class="fs-mini">{{unscheduledTreatmentDataPercentCompleted}}% Completed</span>
            </div>
            <b-progress v-if="allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL.PropertyValue > 0" variant="success" class="bg-gray-lighter progress-xs mb-4" :value="unscheduledTreatmentDataPercentCompleted" :max="100" />
            <AdminTable v-if="unscheduledTreatmentDataToday" :data="unscheduledTreatmentDataToday" :columns="unscheduledTreatmentColumns" :options="options" :extra="this.getExtraParams('unscheduled-treatment')" />
          </template>
          <EmptyState v-else :restricted="true" subject="unscheduled_treatment" />
       </Widget>
      </b-col>
   </b-row>

   <!-- start of second row of data tables -->
   <b-row v-if="allSettings && employees" v-show="!busy && allSettings">
     <b-col lg="6" xs="12">
        <Widget>
          <b-row>
            <b-col xs="7">
              <h5><span class='fw-semi-bold'>Broken Appointments</span></h5>
            </b-col>
            <b-col xs="5">
              <div class="post-user mt-n-sm">
                <span class="thumb-xs mr mt-n-sm">
                  <!--<img class="rounded-circle" src='../../assets/people/a4.jpg' alt="..." />-->
                  <span class="glyphicon glyphicon-user"></span>
                </span>
                  <p class="fw-semi-bold mb-xs pull-right mb-4 responsible-person-badge" v-if="brokenAppointmentResponsible" v-b-tooltip.hover :title="brokenAppointmentResponsible.all.join(', ')">
                    {{ brokenAppointmentResponsible.first }}<span v-if="brokenAppointmentResponsible.count > 1" class="responsible-count">&nbsp;+{{ brokenAppointmentResponsible.count - 1 }}</span>
                  </p>
              </div>
            </b-col>
          </b-row>
          <template v-if="$can('view', 'broken_appointment') || $can('view', 'all')">
            <AdminTable v-if="brokenAppointmentsDataToday" :data="brokenAppointmentsDataToday" :columns="brokenAppointmentsColumns" :options="options" :extra="this.getExtraParams('broken-appointments')"/>
          </template>
          <EmptyState v-else :restricted="true" subject="broken_appointment" />
       </Widget>
     </b-col>
     <b-col lg="6" xs="12">
        <Widget>
          <b-row>
            <b-col xs="7">
              <h5><span class='fw-semi-bold'>Outstanding Claims</span></h5>
            </b-col>
            <b-col xs="5">
              <div class="post-user mt-n-sm">

                  <span class="thumb-xs mr mt-n-sm">
                    <!--<img class="rounded-circle" src='../../assets/people/a4.jpg' alt="..." />-->
                    <span class="glyphicon glyphicon-user"></span>
                  </span>
                  <p class="fw-semi-bold mb-xs pull-right mb-4 responsible-person-badge" v-if="outstandingClaimResponsible" v-b-tooltip.hover :title="outstandingClaimResponsible.all.join(', ')">
                    {{ outstandingClaimResponsible.first }}<span v-if="outstandingClaimResponsible.count > 1" class="responsible-count">&nbsp;+{{ outstandingClaimResponsible.count - 1 }}</span>
                  </p>
              </div>
            </b-col>
          </b-row>
          <template v-if="$can('view', 'outstanding_claim') || $can('view', 'all')">
            <AdminTable v-if="outstandingClaimsDataToday" :data="outstandingClaimsDataToday" :columns="outstandingClaimsColumns" :options="outstandingClaimsOptions" :extra="this.getExtraParams('outstanding-claims')" />
          </template>
          <EmptyState v-else :restricted="true" subject="outstanding_claim" />
       </Widget>
     </b-col>

   </b-row>

   <!-- start of third row of data tables -->
   <b-row v-if="allSettings && employees" v-show="!busy && allSettings">
     <b-col lg="6" xs="12">
        <Widget>
          <b-row>
            <b-col xs="7">
              <h5><span class='fw-semi-bold'>Patient Balances</span></h5>
            </b-col>
            <b-col xs="5">
              <div class="post-user mt-n-sm">
                <span class="thumb-xs mr mt-n-sm">
                  <!--<img class="rounded-circle" src='../../assets/people/a4.jpg' alt="..." />-->
                  <span class="glyphicon glyphicon-user"></span>
                </span>
                <p class="fw-semi-bold mb-xs pull-right mb-4 responsible-person-badge" v-if="patientBalancesResponsible" v-b-tooltip.hover :title="patientBalancesResponsible.all.join(', ')">
                  {{ patientBalancesResponsible.first }}<span v-if="patientBalancesResponsible.count > 1" class="responsible-count">&nbsp;+{{ patientBalancesResponsible.count - 1 }}</span>
                </p>
              </div>
            </b-col>
          </b-row>
          <template v-if="$can('view', 'patient_balances') || $can('view', 'all')">
            <AdminTable v-if="patientBalancesDataToday" :data="patientBalancesDataToday" :columns="patientBalancesColumns" :options="options" :extra="this.getExtraParams('patient-balances')" />
          </template>
          <EmptyState v-else :restricted="true" subject="patient_balances" />
       </Widget>
     </b-col>
     <b-col lg="6" xs="12">
         <Widget>
          <b-row>
            <b-col xs="7">
              <h5><span class='fw-semi-bold'>Outstanding Pre-Auths</span></h5>
            </b-col>
            <b-col xs="5">
              <div class="post-user mt-n-sm">
                <span class="thumb-xs mr mt-n-sm">
                  <!--<img class="rounded-circle" src='../../assets/people/a4.jpg' alt="..." />-->
                  <span class="glyphicon glyphicon-user"></span>
                </span>
                <p class="fw-semi-bold mb-xs pull-right mb-4 responsible-person-badge" v-if="outstandingPreAuthsResponsible" v-b-tooltip.hover :title="outstandingPreAuthsResponsible.all.join(', ')">
                  {{ outstandingPreAuthsResponsible.first }}<span v-if="outstandingPreAuthsResponsible.count > 1" class="responsible-count">&nbsp;+{{ outstandingPreAuthsResponsible.count - 1 }}</span>
                </p>
              </div>
            </b-col>
          </b-row>
          <template v-if="$can('view', 'outstanding_preauths') || $can('view', 'all')">
            <AdminTable v-if="outstandingPreAuthDataToday" :data="outstandingPreAuthDataToday" :columns="outstandingPreAuthColumns" :options="outstandingPreAuthOptions" :extra="this.getExtraParams('outstanding-preauth')" />
          </template>
          <EmptyState v-else :restricted="true" subject="outstanding_preauths" />
       </Widget>
      </b-col>
     <!--
     <b-col lg="6" xs="12">
         <Widget>
          <b-row>
            <b-col xs="7">
              <h5><span class='fw-semi-bold'>Received Pre-Auths</span></h5>
            </b-col>
            <b-col xs="5">
              <div class="post-user mt-n-sm">
                <span class="thumb-xs mr mt-n-sm"> 
                  
                  <span class="glyphicon glyphicon-user"></span>
                </span>
                <p class="fw-semi-bold mb-xs pull-right mb-4" v-if="processedPreAuthsResponsiblePerson">{{processedPreAuthsResponsiblePerson}}</p>
                
              </div>
            </b-col>
          </b-row>
          <AdminTable v-if="receivedPreAuthDataToday" :data="receivedPreAuthDataToday" :columns="receivedPreAuthColumns" :options="options" :extra="this.getExtraParams('processed-preauths')"/>              
       </Widget>        
      </b-col>
    --> 
   </b-row> 
   <!-- start of fourth row of data tables 
   <b-row v-if="allSettings && employees" v-show="!busy && allSettings">
     
     <b-col lg="6" xs="12">
         <Widget style="background-color:#e9ecef;">
          <b-row>
            <b-col xs="12">
              <h5><span class='fw-semi-bold'>Post-op follow-ups<span style="color:#218bc3;;"> (Coming Soon !)</span></span></h5>
            </b-col>
            
          </b-row>
         <table class="table mt">
           <thead>
             <tr>
               <th>
                 
               </th>
               <th>Patient</th>
               <th>Assigned to</th>
               <th>Procedure</th>
               <th>Follow-up Plan</th>               
             </tr>
           </thead>
           <tbody>
             <tr>
               <td>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox2" disabled
                   />
                   <label for="checkbox2" />
                 </div>
               </td>
               <td>James Doe</td>
               <td>
                  Nilay Shah
               </td>
               <td>
                 D4341 - Scaling and Root planing
               </td>
               <td>
                  <div ><span class="badge text-white badge-danger badge-pill">Due Today</span></div>
               </td>
               
             </tr>
             <tr>
               <td>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox2" disabled
                   />
                   <label for="checkbox2" />
                 </div>
               </td>
               <td>Mark Murphy</td>
               <td>
                  Tina Jones
               </td>
               <td>
                D7953 - Bone grafting
               </td>
               <td>
                  <div ><span class="badge text-white badge-danger badge-pill">Due Today</span></div>
               </td>
               
             </tr>
             <tr>
               <td>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox2" disabled
                   />
                   <label for="checkbox2" />
                 </div>
               </td>
               <td>John Doe</td>
               <td>
                  Nilay Shah
               </td>
               <td>
                 D6010 - Implant Placement
               </td>
               <td>
                  <div ><span class="badge text-white badge-danger badge-pill">Due Today</span></div>
               </td>
               
             </tr>
            
           </tbody>
         </table>
         
       </Widget>
        
      </b-col>



      <b-col lg="6" xs="12">
        <Widget style="background-color:#e9ecef;">
          <b-row>
            <b-col><h5><span class='fw-semi-bold'>Routines<span style="color:#218bc3;;"> (Coming Soon !)</span></span></h5></b-col>
          </b-row>
         <table class="table">
           <thead>
             <tr>
               <th>
               </th>
               <th>Task</th>
               <th>Repeats</th>
               <th>Assigned to</th>
             </tr>
           </thead>
           <tbody>
             <tr>
               <td>
                <div class="abc-checkbox">
                   <input type="checkbox" disabled
                     id="checkbox1"
                   />
                   <label for="checkbox1" />
                 </div>
               </td>
               <td>Autoclave maintenance</td>
               <td>
                  <i class='glyphicon glyphicon-retweet mr'></i>Every 1st Wednesday of the month
               </td>
               <td>
                 Christina Jones
               </td>
             </tr>
             <tr>
               <td>
                <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox1" disabled
                   />
                   <label for="checkbox1" />
                 </div>
               </td>
               <td>Changing out Suction Valves</td>
               <td>
                  <i class='glyphicon glyphicon-retweet mr'></i>Every Monday
               </td>
               <td>
                 Christina Jones
               </td>
             </tr>
             <tr>
               <td>
                <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox1" disabled
                   />
                   <label for="checkbox1" />
                 </div>
               </td>
               <td>Update Order List </td>
               <td>
                  <i class='glyphicon glyphicon-retweet mr'></i>Every other Wednesday
               </td>
               <td>
                 Nicole Adams
               </td>
             </tr>
           </tbody>
         </table>
         
       </Widget>
     </b-col>

   </b-row>   
   -->
   
   <!-- start of fifth row of data tables

   <b-row v-if="allSettings && employees" v-show="!busy && allSettings">
     <b-col lg="6" xs="12">
        <Widget>
          <b-row>
            <b-col xs="7">
              <h5><span class='fw-semi-bold'>Premed Reminders</span></h5>
            </b-col>
            
            <b-col xs="5">
              <div class="post-user mt-n-sm">
                  
                  <span class="thumb-xs mr mt-n-sm"> 
                    <img class="rounded-circle" src='../../assets/people/a4.jpg' alt="..." />
                  </span>
                  <p class="fw-semi-bold mb-xs pull-right ">Maryna Nison</p>
                  <p class="fs-mini text-muted mb-0"><small>Treatment Coordinator</small></p>

              </div>
            </b-col>
          </b-row>
         <table class="table mt">
           <thead>
             <tr>
               <th>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox1"
                   />
                   <label for="checkbox1" />
                 </div>
               </th>
               <th>Patient</th>
               <th>Provider</th>
               <th>Appointment Description</th>
               <th>Status</th>
             </tr>
           </thead>
           <tbody>
             <tr>
               <td>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox2"
                   />
                   <label for="checkbox2" />
                 </div>
               </td>
               <td>Mark</td>
               <td>
                  Nilay Shah
               </td>
               <td>
                 D4341 - Scaling and Root planing
               </td>
               <td>
                  <div><span class="badge badge-secondary">Left Msg on Ans. Mach</span></div>
               </td>
               
             </tr>
             <tr>
               <td>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox2"
                   />
                   <label for="checkbox2" />
                 </div>
               </td>
               <td>Mark</td>
               <td>
                  Nilay Shah
               </td>
               <td>
                 D4341 - Scaling and Root planing
               </td>
               <td>
                  <div ><span class="badge text-white badge-danger badge-pill">Due Today</span></div>
               </td>
               
             </tr>
             <tr>
               <td>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox2"
                   />
                   <label for="checkbox2" />
                 </div>
               </td>
               <td>Mark</td>
               <td>
                  Nilay Shah
               </td>
               <td>
                 D4341 - Scaling and Root planing
               </td>
               <td>
                  <div ><span class="badge text-white badge-danger badge-pill">Due Today</span></div>
               </td>
               
             </tr>
            
           </tbody>
         </table>
         
       </Widget>
     </b-col>
     
   </b-row>   
   
  -->

    </template>
    <!-- The new branch's ONE slide-out outlet (the follow-up drawer and the new task drawer).
         The legacy branch's outlets live inside AdminTable; v-if / v-else keeps the two from ever being mounted together. -->
    <slideout-panel v-if="fuUseNewUi"></slideout-panel>
  </div>
</template>

<script>
/* eslint-disable */
  import Vue from 'vue';
  import Widget from '@/components/Widget/Widget';
  import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
  import AdminPageSkeleton from './AdminPageSkeleton.vue';
  import mixin from '../../mixin.js';
  import AdminTable from './AdminTable';
  import moment from 'moment-timezone';
  import _ from 'lodash';
  import TodoItem from '../TaskList/components/TodoItem';
  import TodoDetails from '../TaskList/components/TodoDetails';
  import TaskDrawer from '../TaskList/components/TaskDrawer.vue';
  import EmptyState from '@/components/EmptyState/EmptyState.vue';
  import { getResponsibleEmployees } from '@/utils/permissions';
  import { cleanTaskContent } from '@/utils/tasks/completeTask.js';
  import { firstName } from '@/utils/tasks/composerChips.js';
  // Admin "Today" redesign (design 3a, Phase A2) — the new branch only. See fuUseNewUi.
  import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
  import AdminWorklist from '@/components/Followups/AdminWorklist.vue';
  import AdminDoneRail from '@/components/Followups/AdminDoneRail.vue';
  import FuIcon from '@/components/Followups/FuIcon.vue';
  import { shouldUseNewFollowupsUi, browserStorage } from '@/utils/followups/uiVersion.js';
  import { switchUi } from '@/utils/followups/uiSwitch.js';
  import { queueByKey, responsibleEmployeeIds } from '@/utils/followups/queues.js';
  import {
    DASHBOARD_QUEUES, DEFAULT_TIME_ZONE, SORT_KEYS, splitDashboardRows, myQueueKeys, doneRail, callGoals,
  } from '@/utils/followups/dashboard.js';

  // The quiet re-fetch after a save waits this long, so a burst of saves costs one query.
  const FU_REFRESH_DELAY_MS = 800;
  const FU_SORT_LABELS = { recent: 'Most recent first', oldest: 'Oldest first', amount: 'Largest amount first' };

  export default {
    name: 'AdminPage',
    components: { Widget, AdminPageSkeleton, AdminTable, TodoItem, EmptyState, FollowupsUiToggle, AdminWorklist, AdminDoneRail, FuIcon },
    mixins: [mixin],

    mounted() {
      //this.mountSlick()
      this.fetchToDos();
    },

    created() {      
      if(this.allSettings)
        this.init();
    },

    data() {
      return { 
        busy: false,
        results: [],
        selectedPatients: [],
        options: {
          filterable: [],
          skin:'table',
          sortable: [],
          perPage: 10,
          sortByColumn: false,
          pagination: { chunk: 10, dropdown: false },
          rowClassCallback: function(row) {
            return row.teamioActive ? 'selectedRow' : '';
          }
          //columnsClasses: {id:'w'}
        },
        outstandingClaimsOptions: {
          skin:'table',
          sortable: [],
          perPage: 10,
          filterByColumn: false,
          filterable: ['Patient', 'Carrier'],
          sortByColumn: false,
          pagination: { chunk: 10, dropdown: false },
          texts: {
            filter: 'Filter by Patient or Carrier'
            //count:' '
          },
          rowClassCallback: function(row) {
            return row.teamioActive ? 'selectedRow' : '';
          }
        },
        outstandingPreAuthOptions: {
          skin:'table',
          sortable: [],
          perPage: 10,
          filterByColumn: false,
          filterable: ['Patient', 'Carrier'],
          sortByColumn: false,
          pagination: { chunk: 10, dropdown: false },
          texts: {
            filter: 'Filter by Patient or Carrier'
            //count:' '
          },
          rowClassCallback: function(row) {
            return row.teamioActive ? 'selectedRow' : '';
          }
        },
        allMyTasksLoadingStatus: [].repeat(false, 30),
        wikiPages: [],
        mytasksColumns: ['id', 'taskContent'],
        unscheduledTreatmentColumns: ['id', 'Patient', 'FollowUpActionPlan', 'Attempts'],
        brokenAppointmentsColumns: ['id', 'Patient', 'FollowUpActionPlan', 'Attempts'],
        receivedPreAuthColumns: ['id', 'Patient', 'FollowUpActionPlan', 'Attempts'],
        outstandingClaimsColumns: ['id', 'Patient', 'FollowUpActionPlan', 'Attempts'],
        outstandingPreAuthColumns: ['id', 'Patient', 'FollowUpActionPlan', 'Attempts'],
        patientBalancesColumns: ['id', 'Patient', 'amount', 'FollowUpActionPlan', 'Attempts'],
        postOpFollowUpsColumns: ['id', 'Patient', 'Amount', 'FollowUpActionPlan', 'Attempts'],
        preMedRemindersColumns: ['id', 'Patient', 'Amount', 'FollowUpActionPlan', 'Attempts'],
        routinesColumns: [],

        // ---- Admin "Today" redesign (new branch only) ----
        fuUiVersion: 0,          // bumped by the Classic / New view links so fuUseNewUi re-reads storage
        fuToday: null,           // the `d` the shown ADMIN_DASHBOARD result was asked for
        fuLoading: false,        // the page load's ADMIN_DASHBOARD is out (busy alone is also cleared by fetchToDos)
        fuScope: 'everyone',     // 'everyone' | 'mine' (My queues)
        fuSort: 'recent',        // dashboard.js SORT_KEYS
        fuClock: Date.now(),     // re-read by the greeting / eyebrow; ticks only while the new branch shows
        fuRefreshDelayMs: FU_REFRESH_DELAY_MS,
        fuDoneSubscribed: false, // tasks/subscribeCompletedTasksToday is running for this page
        fuGoneTaskIds: [],       // tasks the drawer deleted, hidden from "Done today" before the listener catches up
      }
    },

    methods: {
      ...mapActions('teamio', ['runQuery']),
      ...mapActions('wiki', ['getWikiPages', 'getSiteTags']),
      ...mapActions('tasks', ['getTasksForEmployee', 'getTasksForSite', 'getTaskDetails', 'addPersonalTask', 'updateTask', 'updateMultipleTasks', 'deleteTask', 'deleteThisInstanceOfRepeatedTask']),
      ...mapMutations('teamio', ['emptyResults']),

      // Shared helper for every xxxResponsible computed. Returns
      // {first, all, count} or null. See utils/permissions.getResponsibleEmployees.
      _responsibleEmployeesFor(settingKey) {
        if (!this.allSettings || !this.allSettings[settingKey]) return null;
        return getResponsibleEmployees(this.employees, this.allSettings[settingKey].PropertyValue);
      },

      // opts.quiet (the new Today page's refresh after a save, fuRefresh): no skeleton, no
      // emptyResults, no wiki re-read, and an empty / failed / out-of-date answer keeps the rows that
      // are on screen. Without it (created, the allSettings watcher) this is the page's load, as before.
      async init(opts) {
        const quiet = !!(opts && opts.quiet === true);
        const seq = (this.fuQuerySeq || 0) + 1;
        this.fuQuerySeq = seq;
        if (!quiet) {
          this.busy = true;
          this.fuLoading = true;
        }
        let query = {
          type: 'ADMIN_DASHBOARD',
          siteID: this.siteID,
          d: moment().format('YYYY-MM-DD')
        };
        if (!quiet) this.emptyResults();
        try {
          const res = await this.runQuery(query);
          if (!quiet) {
            this.results = res;
            this.fuToday = query.d;
            let temp = await this.getWikiPages({siteID: this.siteID});
            if(temp.length > 0)
              this.wikiPages = temp[0];
          } else if (seq === this.fuQuerySeq && res && res.data && res.data.length > 0) {
            this.results = res;
            this.fuToday = query.d;
            this.fuClock = Date.now();
          }
        } finally {
          if (!quiet) {
            this.busy = false;
            this.fuLoading = false;
          }
        }
      },

      async fetchToDos () {
        this.busy = true;
        try {
          if(this.myCreatedTasks.length === 0 || this.assignedToMeTasks.length === 0 || this.myEmployeesTasks.length === 0) {
            if(this.user.Role === 'Super Admin' || this.user.Role === 'Admin') {
              await this.getTasksForSite({siteID: this.siteID, employeeID: this.userID});
            } else {
              await this.getTasksForEmployee({siteID: this.siteID, employeeID: this.userID});
            }
          }
        } finally {
          this.busy = false;
        }
      },

      async deleteTaskItem(item) {
        //console.log(`trying to delete item: ${JSON.stringify(item)}`);
        await this.deleteTask(
          { 
            id: item.id,
            deleted: true,
            deletedBy: this.userID,
            deletedOn: moment().toDate(),
          });
      },

      async deleteThisInstanceOnly(item) {
        let payload = {
          task : item,
          //clientDateFormat : this.allSettings.GENERAL_TIMEZONE.PropertyValue.toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'
          clientDateFormat: this.dateFormatStr || 'MM/DD/YYYY'
        }
        //console.log(`payload: ${JSON.stringify(payload)}`);
        await this.deleteThisInstanceOfRepeatedTask(payload);
      },

      async editTaskItem(item, index, taskType) {        
        console.log('i am in edit item', item, index, taskType);
        let temp;
        switch(taskType) {
          case 'allMyTasksDueToday': temp = this.allMyTasksLoadingStatus; break;
        }

        Vue.set(temp, index, true);
        let task;
        try {
          task = await this.getTaskDetails({taskID: item.id});
          //console.log(`task details: ${JSON.stringify(task)}`);
          //console.log(`got task details`);

          if(!task.assignees) {
            let temp2 = [];    
            temp2.push(task.assignedToEmpID);
            Vue.set(task, 'assignees', temp2);
          }

          /*
            Adding a patch because I noticed sometimes, some sites have some tasks that have wrong completedAt date and that messes up when they try to mark them completed
          */
          if(task.completedAt) {
            try {
              let temp = task.completedAt.toISOString();
              //console.log('able to convert completedAt to ISO String. No issue', temp);
            } catch (error) {
              // if we catch an error here then we have an invalid date here. let's remove it
              delete task.completedAt;
              delete task.completedAtStr;
            }
          }

          const panel1Handle = this.$showPanel({
            component : TodoDetails,
            openOn: 'right',
            width: 750,
            props: {
              task: task,
              wikiPages: this.wikiPages,
              patients: this.patients,
            }
          })

          panel1Handle.promise
            .then(result => {
              //console.log(`panel handle promise done`);
              Vue.set(temp, index, false);
          });
        } catch(error) {
          console.log(`Error while trying to edit task: ${error}`);
          Vue.set(temp, index, true);
        }
      },

      async handleItemChange (item) {        
        console.log('in handleItemChange', item);
        /*
          Adding a patch because I noticed sometimes, some sites have some tasks that have wrong completedAt date and that messes up when they try to mark them completed
        */
        if(item.completedAt) {
          try {
            let temp = item.completedAt.toISOString();
            //console.log('able to convert completedAt to ISO String. No issue', temp);
          } catch (error) {
            // if we catch an error here then we have an invalid date here. let's remove it
            delete item.completedAt;
            delete item.completedAtStr;
          }
        }
        await this.updateTask(item);
      },

      // ---- Admin "Today" redesign (new branch only) -------------------------------------------

      /** The "Classic view" / "New view" links: a Super Admin re-renders in place, an office user reloads (uiSwitch.js). */
      fuSetUi(target) {
        if (switchUi('admin', target, { isSuperAdmin: this.isSuperAdmin, userKey: this.userID }) === 'rerender') this.fuUiVersion += 1;
      },

      /** The old widgets' permission rule, as exactly true / false (dashboard.js needs === true). */
      fuCanView(subject) {
        return !!(this.$can && (this.$can('view', subject) || this.$can('view', 'all')));
      },

      /**
       * A task's text was clicked: the new task drawer (TaskDrawer, 540 wide) — the same panel the
       * new Tasks page opens. The classic branch's "My Tasks" keeps editTaskItem → TodoDetails.
       * The drawer reads the full doc + comments itself (tasks/getTaskDetails on mount), so this
       * hands it the store's copy of the task plus the page's wiki pages, patients and site tags.
       * No list listeners: "Your tasks" is the store getter over the Firestore-fed lists, so a save
       * or delete reaches it through the listener, as with editTaskItem's TodoDetails.
       */
      fuOpenTask(task) {
        if (!task || !task.id) return;
        const list = this.todaysTasksForEmployee(this.userID) || [];
        const listed = list.find(t => t && String(t.id) === String(task.id));
        // ⛔ Callbacks as PROPS: vue2-slideout-panel binds only `closePanel`, so $showPanel's
        // `listeners` would be silently ignored (TaskDrawer.vue header).
        this.$showPanel({
          component: TaskDrawer,
          openOn: 'right',
          width: 540,
          props: {
            task: Object.assign({}, listed || task),
            wikiPages: this.wikiPages || [],
            patients: this.patients || [],
            siteTags: this.fuSiteTags,
            onTaskUpdated: updated => this.fuTaskUpdated(updated),
            onTaskDeleted: payload => this.fuTaskDeleted(payload),
          },
        });
      },

      /** The drawer saved a task. The lists are listener-fed; only a delete needs hiding at once. */
      fuTaskUpdated(task) {
        if (task && task.id && task.deleted === true) this.fuTaskDeleted({ id: task.id });
      },

      /** The drawer deleted a task: out of "Done today" now, before the listener's snapshot. */
      fuTaskDeleted(payload) {
        const id = payload && payload.id;
        if (id === null || id === undefined || id === '') return;
        if (this.fuGoneTaskIds.indexOf(String(id)) === -1) this.fuGoneTaskIds.push(String(id));
      },

      /**
       * "Done today" tasks: (re)start tasks/subscribeCompletedTasksToday for this site + office day,
       * or stop it. Driven by the fuDoneSubscriptionKey watcher; never runs for the classic page.
       */
      fuSyncDoneTasks(key) {
        if (key) {
          this.fuDoneSubscribed = true;
          this.$store.dispatch('tasks/subscribeCompletedTasksToday', { siteID: this.siteID, timeZone: this.fuTimeZone });
        } else if (this.fuDoneSubscribed) {
          this.fuDoneSubscribed = false;
          this.$store.dispatch('tasks/unsubscribeCompletedTasksToday');
        }
      },

      /** A follow-up was saved / logged: re-read ADMIN_DASHBOARD quietly, once per burst of saves. */
      fuScheduleRefresh() {
        if (this.fuRefreshTimer) clearTimeout(this.fuRefreshTimer);
        this.fuRefreshTimer = setTimeout(() => {
          this.fuRefreshTimer = null;
          this.fuRefresh();
        }, this.fuRefreshDelayMs);
      },

      fuRefresh() {
        return this.init({ quiet: true });
      },

      fuStopClock() {
        if (this.fuClockTimer) clearInterval(this.fuClockTimer);
        this.fuClockTimer = null;
      },
    },

    computed: {

      ...mapState({
         patients: state => state.customer.patients,
         myCreatedTasks: state => state.tasks.myCreatedTasks,
         assignedToMeTasks: state => state.tasks.assignedToMeTasks,
         myEmployeesTasks: state => state.tasks.myEmployeesTasks,
         fuCompletedTasksToday: state => state.tasks.completedTasksToday
      }),

      ...mapGetters({
        'allMyTasksDueToday' : 'tasks/allMyTasksDueToday',
        'todaysTasksForEmployee': 'tasks/todaysTasksForEmployee'
      }), 

      adminData() {
        if(this.results && this.results.data && this.results.data.length > 0)
          return this.results.data;
      },

      overdueRecallDataToday() {
        if(this.adminData) {
           let temp = this.adminData[0].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.OverdueRecallStatusUpdatedAt).isSame(moment(), 'day') ||  t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
             /* The reason for the check below is because when the data comes from DB, it's string and then i convert to boolean locally. 
             So if I need to check the value again, I need to first check if it's boolean or not */
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        }
      },

      overdueRecallDataPercentCompleted() {
        if(this.adminData && this.allSettings) {
          return ((this.adminData[0].filter(t => t.completed === true).length /this.allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL.PropertyValue)*100).toFixed(0);
        }
      },

      unscheduledTreatmentDataToday() {
        if(this.adminData) {
           let temp = this.adminData[1].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.UnscheduledTreatmentStatusUpdatedAt).isSame(moment(), 'day') || t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        }
      },

      unscheduledTreatmentDataPercentCompleted() {
        if(this.adminData)
          return ((this.adminData[1].filter(t => t.completed === true).length/this.allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL.PropertyValue)*100).toFixed(0);
      },

      brokenAppointmentsDataToday() {        
        if(this.adminData) {
          //console.log(`brokenAppointmentsDataToday called with data length ${this.adminData[2].length}: ${JSON.stringify(this.adminData[2], null, 2)}`);
           let temp = this.adminData[2].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.BrokenAppointmentStatusUpdatedAt).isSame(moment(), 'day') || t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        }
      },

      outstandingClaimsDataToday() {
        if(this.adminData) {
           let temp = this.adminData[3].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.OutstandingClaimStatusUpdatedAt).isSame(moment(), 'day') || t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        } 
      },

      receivedPreAuthDataToday() {
        if(this.adminData) {
           let temp = this.adminData[4].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.ProcessedPreAuthStatusUpdatedAt).isSame(moment(), 'day') || t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        }
      },
      
      outstandingPreAuthDataToday() {
        if(this.adminData) {
           let temp = this.adminData[5].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.OutstandingPreAuthStatusUpdatedAt).isSame(moment(), 'day') || t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        }  
      },

      patientBalancesDataToday() {
        if(this.adminData) {
           let temp = this.adminData[6].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.PatientBalancesStatusUpdatedAt).isSame(moment(), 'day') || t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        }
      },

      // Each *_RESPONSIBLE_EMPLOYEEID setting holds a pipe-delimited list of
      // EmployeeIDs ("empA|empB|empC"). The old code used `===` against the
      // whole pipe-string, which only matched for single-employee setups and
      // silently went blank everywhere else. The new helper returns
      // {first, all, count} so the template can render "first name + N more"
      // with a tooltip listing every responsible employee. Also fixes two
      // wrong setting keys (PROCESSED_PREAUTHS_EMPLOYEEID and
      // OUTSTANDING_PREAUTHS_EMPLOYEEID were missing the RESPONSIBLE_ prefix —
      // those computed properties were ALWAYS undefined before this).
      overdueRecallResponsible() {
        return this._responsibleEmployeesFor('OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID');
      },

      unscheduledTreatmentResponsible() {
        return this._responsibleEmployeesFor('UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID');
      },

      brokenAppointmentResponsible() {
        return this._responsibleEmployeesFor('BROKEN_APPOINTMENTS_RESPONSIBLE_EMPLOYEEID');
      },

      outstandingClaimResponsible() {
        return this._responsibleEmployeesFor('OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID');
      },

      patientBalancesResponsible() {
        return this._responsibleEmployeesFor('PATIENT_BALANCES_RESPONSIBLE_EMPLOYEEID');
      },

      processedPreAuthsResponsible() {
        return this._responsibleEmployeesFor('PROCESSED_PREAUTHS_RESPONSIBLE_EMPLOYEEID');
      },

      outstandingPreAuthsResponsible() {
        return this._responsibleEmployeesFor('OUTSTANDING_PREAUTHS_RESPONSIBLE_EMPLOYEEID');
      },

      // ---- Admin "Today" redesign (new branch only) -------------------------------------------

      /**
       * Which template branch renders. An office user → STAGE.admin and their own choice (the old page at "preview"). A Super Admin → the new page unless "Classic view" was picked.
       */
      fuUseNewUi() {
        // eslint-disable-next-line no-unused-expressions
        this.fuUiVersion; // re-read storage after the Classic / New view link
        return shouldUseNewFollowupsUi('admin', { isSuperAdmin: this.isSuperAdmin, userKey: this.userID, storage: browserStorage() });
      },

      fuBusy() {
        return !!(this.busy || this.fuLoading || !this.allSettings);
      },

      fuTimeZone() {
        const s = this.allSettings && this.allSettings.GENERAL_TIMEZONE;
        const tz = s && s.PropertyValue ? String(s.PropertyValue) : '';
        return tz && moment.tz.zone(tz) ? tz : DEFAULT_TIME_ZONE;
      },

      fuNow() {
        return moment(this.fuClock).tz(this.fuTimeZone);
      },

      fuEyebrow() {
        return this.fuNow.format('dddd, MMMM D').toUpperCase();
      },

      fuFirstName() {
        const u = this.user || {};
        if (u.FName && String(u.FName).trim()) return String(u.FName).trim();
        const name = String(u.Name || '').trim();
        if (!name) return '';
        // "Last, First" → First; "First Last" → First; "Dr. First Last" → First (shared firstName skips the title).
        const part = name.indexOf(',') > -1 ? name.slice(name.indexOf(',') + 1).trim() : name;
        return firstName(part);
      },

      fuGreeting() {
        const h = this.fuNow.hour();
        const part = h < 12 ? 'morning' : (h < 17 ? 'afternoon' : 'evening');
        const name = this.fuFirstName.replace(/\.$/, '');
        return name ? `Good ${part}, ${name}.` : `Good ${part}.`;
      },

      fuSortOptions() {
        return SORT_KEYS.map(key => ({ key, label: FU_SORT_LABELS[key] || key }));
      },

      /** The queues this user may see (the old widgets' $can subjects). */
      fuVisibleQueueKeys() {
        return DASHBOARD_QUEUES.filter(q => this.fuCanView(q.subject)).map(q => q.key);
      },

      /** { queueKey: [EmployeeID, …] } from each queue's *_RESPONSIBLE_EMPLOYEEID setting. */
      fuResponsibleIds() {
        const out = {};
        DASHBOARD_QUEUES.forEach((q) => { out[q.key] = responsibleEmployeeIds(queueByKey(q.key), this.allSettings); });
        return out;
      },

      /** "My queues": the queues this user is responsible for. None ⇒ an empty My queues, no fallback. */
      fuMyQueueKeys() {
        return myQueueKeys(this.fuResponsibleIds, this.userID);
      },

      fuSplitAll() {
        return splitDashboardRows(this.adminData, { can: this.fuCanView, today: this.fuToday });
      },

      fuSplit() {
        if (this.fuScope !== 'mine') return this.fuSplitAll;
        return splitDashboardRows(this.adminData, { can: this.fuCanView, queueKeys: this.fuMyQueueKeys, today: this.fuToday });
      },

      /** The chips / rows the worklist may show: permitted, and in My queues also the user's own. */
      fuWorklistQueueKeys() {
        if (this.fuScope !== 'mine') return this.fuVisibleQueueKeys;
        return this.fuVisibleQueueKeys.filter(k => this.fuMyQueueKeys.indexOf(k) > -1);
      },

      fuNoQueues() {
        return this.fuScope === 'mine' && this.fuWorklistQueueKeys.length === 0;
      },

      /** The tag choices the task drawer offers: the site's saved tags + any tag on a loaded task. */
      fuSiteTags() {
        const t = (this.$store.state.tasks) || {};
        const out = new Set();
        const add = (tag) => { if (typeof tag === 'string' && tag.trim()) out.add(tag.trim()); };
        (t.siteTaskTags || []).forEach(add);
        [t.myCreatedTasks, t.assignedToMeTasks, t.myEmployeesTasks].forEach((l) => {
          (l || []).forEach((task) => { if (task && Array.isArray(task.tags)) task.tags.forEach(add); });
        });
        return Array.from(out).sort();
      },

      fuTasks() {
        const list = this.todaysTasksForEmployee(this.userID);
        return Array.isArray(list) ? list : [];
      },

      fuRail() {
        return doneRail(this.fuSplit.done, { userID: this.userID });
      },

      /** The office's calendar day — rolls over at the office's midnight (the minute clock). */
      fuOfficeDay() {
        return this.fuNow.format('YYYY-MM-DD');
      },

      /**
       * What the "Done today" tasks listener is for: site + office zone + office day, or null when
       * it must not run (classic page, no site, settings not loaded yet). It keys on the new view, not
       * the role, so it starts for every office user on the new Today page. A change of key
       * re-subscribes — so the listener moves to the new day at the office's midnight.
       */
      fuDoneSubscriptionKey() {
        if (!this.fuUseNewUi || !this.siteID || !this.allSettings) return null;
        return `${this.siteID}|${this.fuTimeZone}|${this.fuOfficeDay}`;
      },

      /**
       * Tasks finished today for the "Done today" rail: not deleted, and in My queues only the ones
       * the user completed. { id, text, completedByEmpID, task } — text cleaned like the task rows.
       */
      fuDoneTasks() {
        const list = Array.isArray(this.fuCompletedTasksToday) ? this.fuCompletedTasksToday : [];
        const me = String(this.userID);
        const gone = this.fuGoneTaskIds;
        const doc = typeof document !== 'undefined' ? document : null;
        return list
          .filter(t => t && t.id && t.deleted !== true && t.status === 'COMPLETED' && gone.indexOf(String(t.id)) === -1)
          .filter(t => this.fuScope !== 'mine' || String(t.completedByEmpID) === me)
          .map(t => ({
            id: t.id,
            text: cleanTaskContent(t.content, doc).text || 'Untitled task',
            completedByEmpID: t.completedByEmpID === undefined ? null : t.completedByEmpID,
            task: t,
          }));
      },

      /** The office's goals: EVERYONE's done rows, permitted queues only (dashboard.js callGoals). */
      fuGoals() {
        const val = (key) => {
          const s = this.allSettings && this.allSettings[key];
          return s ? s.PropertyValue : null;
        };
        return callGoals(this.fuSplitAll.done, {
          overdueRecall: val('OVERDUE_RECALL_DAILY_CALLS_GOAL'),
          unscheduledTreatment: val('UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL'),
        }, this.fuVisibleQueueKeys);
      },

      overdueRecallColumns() {
        let cols = ['id', 'Patient'];
        if (
          this.allSettings &&
          this.allSettings.APPT_REMINDER_PAID_FOR &&
          this.allSettings.APPT_REMINDER_PAID_FOR.PropertyValue === 'TRUE'
        ) {
          cols.push('AutoRemindersSent');
        }
        cols.push('FollowUpActionPlan', 'Attempts');
        return cols;
      },
    },

    watch: {
      // The greeting / eyebrow clock runs only while the new branch is on screen.
      fuUseNewUi: {
        immediate: true,
        handler(on) {
          this.fuStopClock();
          if (!on) return;
          this.fuClock = Date.now();
          this.fuClockTimer = setInterval(() => { this.fuClock = Date.now(); }, 60000);
        },
      },

      // "Done today" tasks: subscribe on entering the new branch, re-subscribe on a new office day,
      // unsubscribe on leaving it. null for the classic page ⇒ never subscribes.
      fuDoneSubscriptionKey: {
        immediate: true,
        handler(key) {
          this.fuSyncDoneTasks(key);
        },
      },

      allSettings(newValue, oldValue) {        
        //console.log(`watch changed. Old value: ${JSON.stringify(oldValue)}, \n New value: ${JSON.stringify(newValue)}`);
        if(!oldValue) {
          this.init();
        }        
      }
    },

    beforeDestroy() {
      this.fuStopClock();
      this.fuSyncDoneTasks(null);
      if (this.fuRefreshTimer) clearTimeout(this.fuRefreshTimer);
      this.fuRefreshTimer = null;
    }

  };
</script>
<!--
<style src="./TaskPage.scss" lang="scss" />
-->
<style src="./Basic.scss" lang="scss" />

<!-- Admin "Today" redesign (Phase A2): the new branch's page styles, scoped (`.fu-admin`). -->
<style src="../Followups/admin-page.scss" lang="scss" scoped />

<style>
/* Add keyframes for spinner animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* "+N more" chip appended to the first responsible person's name in each
   section header when multiple employees share the assignment. Hover shows
   the full list via v-b-tooltip. Class is shared with AdminByEmployeePage. */
.responsible-person-badge .responsible-count {
  font-size: 0.75em;
  color: #94a3b8;
  font-weight: 500;
  background-color: #f1f5f9;
  padding: 1px 6px;
  border-radius: 8px;
  margin-left: 2px;
}
</style>




